/**
 * Personel yönetimi — §5.1 rol matrisi, nokta atamaları, kimlik durumu.
 *
 * GV-04 / GV-05: parola_hash, pin_hash ve totp_secret HİÇBİR KOŞULDA sunucudan
 * dönmez; yalnızca "kurulu mu" bilgisi gelir. Bu ekran da o çizgiyi korur —
 * PIN ekranda gösterilmez, gösterilemez.
 *
 * Yetki yükseltme koruması: kimse kendi rolünün üstünde bir rol atayamaz ve
 * kimse kendi rolünü değiştiremez (dört göz ilkesi tek kullanıcıda kırılmasın).
 * Kural sunucuda zorlanır; burada da önceden açıklanır ki kullanıcı reddi bir
 * arıza sanmasın.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  Kart,
  Rozet,
  Secim,
  Tablo,
  TabloBaslik,
  TabloGovde,
  TelefonAlani,
  Uyari,
} from "@kantin/ui";
import {
  ROL_ADI,
  Rol,
  VARSAYILAN,
  telefonGoster,
  telefonMaske,
  yerelGun,
} from "@kantin/shared";
import { aktifOturum, oturumDurumu, useYetkili } from "../../veri/oturum.js";
import { apiOku, apiYaz } from "./api.js";
import { noktaSecenekleri, useNoktalar, useOkul } from "./kancalar.js";
import type { Personel as PersonelKaydi } from "./kancalar.js";
import {
  IslemDurumu,
  Sayfa,
  SayfaBasligi,
  SirKopyala,
  SorguDurumu,
  tarihGoster,
} from "./ortak.js";

/**
 * Bitiş tarihi geçmiş (süresi dolmuş vekalet) atama. Sınır günü yürürlükte
 * sayılır; kıyas okulun saat dilimindeki "bugün" ile yapılır (KK-522).
 * Bunlar listede gösterilmez, denetim izi için Düzenle formunda kalır.
 */
function atamaBitmisMi(a: { bitis: string | null }, bugun: string): boolean {
  return a.bitis !== null && a.bitis.slice(0, 10) < bugun;
}

const ATAMA_ROLU_ADI: Record<number, string> = {
  1: "Kasiyer",
  2: "Nokta sorumlusu",
};

/** §5.1 rol matrisi — panelde görünür olması yetki tartışmasını kısaltır. */
const MATRIS: { yetki: string; hucre: string[] }[] = [
  { yetki: "Satış yapmak", hucre: ["tam", "tam", "yok", "yok", "yok"] },
  {
    yetki: "Satış iptali / iade",
    hucre: ["kosullu", "tam", "yok", "tam", "yok"],
  },
  {
    yetki: "Kasadan nakit bakiye yükleme",
    hucre: ["tam", "tam", "yok", "yok", "yok"],
  },
  {
    yetki: "Yükleme onayı (havale / dekont)",
    hucre: ["yok", "tam", "tam", "tam", "tam"],
  },
  {
    yetki: "Ürün / fiyat tanımı",
    hucre: ["yok", "tam", "okuma", "tam", "okuma"],
  },
  {
    yetki: "Stok sayımı & fire girişi",
    hucre: ["yok", "tam", "okuma", "tam", "okuma"],
  },
  {
    yetki: "Kart eşleştirme / blokaj",
    hucre: ["yok", "tam", "tam", "tam", "yok"],
  },
  {
    yetki: "Limit & yasaklı ürün tanımı",
    hucre: ["yok", "yok", "tam", "tam", "yok"],
  },
  {
    yetki: "Ciro & kâr-zarar raporu",
    hucre: ["yok", "tam", "okuma", "tam", "tam"],
  },
  {
    yetki: "Gelir/gider, cari, alım faturası",
    hucre: ["yok", "tam", "yok", "tam", "tam"],
  },
  {
    yetki: "Denetim kaydı (audit log)",
    hucre: ["yok", "okuma", "okuma", "tam", "okuma"],
  },
];

const MATRIS_SUTUNLARI = [
  "Kasiyer",
  "Kantin müdürü",
  "Okul yönetimi",
  "Okul sistem yöneticisi",
  "Mali müşavir",
];

const HUCRE_METNI: Record<string, string> = {
  tam: "Tam yetki",
  okuma: "Salt okuma",
  kosullu: "Koşullu (üst onay)",
  yok: "Yetkisiz",
};

export function Personel() {
  // §5.1: personel kaydı ve rol ataması KURUM kararıdır. Kantin müdürü listeyi
  // görür ama kendi yetkisini genişletemesin diye yazamaz (OKUL_AYAR kümesi).
  const yazabilir = useYetkili(Rol.OKUL_YONETIMI, Rol.OKUL_SISTEM_YONETICISI);
  const [rolSuzgeci, setRolSuzgeci] = useState("");
  const [noktaSuzgeci, setNoktaSuzgeci] = useState("");
  const [aktifSuzgeci, setAktifSuzgeci] = useState("true");
  const [duzenlenen, setDuzenlenen] = useState<PersonelKaydi | null>(null);
  const [yeniAcik, setYeniAcik] = useState(false);
  const [matrisAcik, setMatrisAcik] = useState(false);

  const noktalar = useNoktalar();
  const okul = useOkul();
  const bugun = yerelGun(
    new Date(),
    okul.data?.saat_dilimi ?? VARSAYILAN.SAAT_DILIMI,
  );
  // Etkin roller = okul ayarı MINUS platform kilidi (0021). Kilitli bir rol
  // seçilebilir görünseydi atamada 400 dönerdi.
  const platformKapaliRoller = okul.data?.platform_kapali_roller ?? [];
  const kullanilabilirRoller = (okul.data?.kullanilabilir_roller ?? []).filter(
    (rol) => !platformKapaliRoller.includes(rol),
  );

  const sorgu = useQuery({
    queryKey: ["personel", "liste", rolSuzgeci, noktaSuzgeci, aktifSuzgeci],
    queryFn: () =>
      apiOku<{ personeller: PersonelKaydi[] }>("/personel", {
        rol: rolSuzgeci || undefined,
        nokta_id: noktaSuzgeci || undefined,
        aktif: aktifSuzgeci || undefined,
      }),
  });

  const personeller = sorgu.data?.personeller ?? [];

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Personel"
        aciklama="Personel kaydı, rol ataması ve nokta yetkileri. Yetki iki seviyelidir: rol NE yapabileceğini, nokta ataması NEREDE yapabileceğini belirler."
        {...(yazabilir
          ? {
              eylem: (
                <Buton
                  tur="birincil"
                  disabled={!okul.data}
                  onClick={() => {
                    setYeniAcik((a) => !a);
                    setDuzenlenen(null);
                  }}
                  aria-expanded={yeniAcik}
                >
                  {yeniAcik ? "Formu kapat" : "Yeni personel"}
                </Buton>
              ),
            }
          : {})}
      />

      {!yazabilir && (
        <Uyari tur="bilgi" baslik="Bu liste size salt okunur açıktır">
          Personel kaydı, rol ataması ve nokta yetkisi yalnızca{" "}
          <strong>{ROL_ADI[Rol.OKUL_YONETIMI]}</strong> rolüyle değiştirilebilir
          — kimse kendi yetkisini genişletemesin diye. Değişiklik için okul
          yönetimine başvurun.
        </Uyari>
      )}

      <Kart
        baslik="Rol matrisi"
        ustBilgi={
          <Buton
            tur="sessiz"
            onClick={() => setMatrisAcik((a) => !a)}
            aria-expanded={matrisAcik}
          >
            {matrisAcik ? "Gizle" : "Göster"}
          </Buton>
        }
      >
        {matrisAcik ? (
          <Tablo>
            <TabloBaslik>
              <th scope="col">Yetki</th>
              {MATRIS_SUTUNLARI.map((s) => (
                <th key={s} scope="col">
                  {s}
                </th>
              ))}
            </TabloBaslik>
            <TabloGovde>
              {MATRIS.map((satir) => (
                <tr key={satir.yetki}>
                  <th scope="row" className="px-3 py-2 text-left font-semibold">
                    {satir.yetki}
                  </th>
                  {satir.hucre.map((h, i) => (
                    <td key={`${satir.yetki}-${i}`}>
                      <Rozet
                        tur={
                          h === "tam"
                            ? "onay"
                            : h === "okuma"
                              ? "bilgi"
                              : h === "kosullu"
                                ? "uyari"
                                : "notr"
                        }
                      >
                        {HUCRE_METNI[h] ?? h}
                      </Rozet>
                    </td>
                  ))}
                </tr>
              ))}
            </TabloGovde>
          </Tablo>
        ) : (
          <p className="m-0 text-sm text-murekkep-2">
            Kasiyer satış yapar ve nakit yükleme alır; ürün/fiyat ve stok kantin
            müdüründedir; limit ve yasaklı ürün tanımı okul yönetimindedir.
            Detay için &quot;Göster&quot;e basın.
          </p>
        )}
      </Kart>

      {yeniAcik && (
        <PersonelFormu
          personel={null}
          kullanilabilirRoller={kullanilabilirRoller}
          kapat={() => setYeniAcik(false)}
          kaydedildi={() => {
            setYeniAcik(false);
            void sorgu.refetch();
          }}
        />
      )}

      {duzenlenen && (
        <PersonelFormu
          personel={duzenlenen}
          kullanilabilirRoller={kullanilabilirRoller}
          kapat={() => setDuzenlenen(null)}
          kaydedildi={() => {
            setDuzenlenen(null);
            void sorgu.refetch();
          }}
        />
      )}

      <Kart baslik="Süzgeçler">
        <div className="grid gap-3 md:grid-cols-3">
          <Secim
            etiket="Rol"
            value={rolSuzgeci}
            onChange={(e) => setRolSuzgeci(e.target.value)}
            secenekler={[
              { deger: "", metin: "Tüm roller" },
              ...kullanilabilirRoller.map((r) => ({
                deger: String(r),
                metin: ROL_ADI[r] ?? "—",
              })),
            ]}
          />
          <Secim
            etiket="Nokta"
            value={noktaSuzgeci}
            onChange={(e) => setNoktaSuzgeci(e.target.value)}
            secenekler={noktaSecenekleri(noktalar.data?.noktalar)}
          />
          <Secim
            etiket="Durum"
            value={aktifSuzgeci}
            onChange={(e) => setAktifSuzgeci(e.target.value)}
            secenekler={[
              { deger: "true", metin: "Aktif" },
              { deger: "false", metin: "Pasif" },
              { deger: "", metin: "Hepsi" },
            ]}
          />
        </div>
      </Kart>

      <Kart baslik="Personel listesi">
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={personeller.length === 0}
          bosBaslik="Personel bulunamadı"
          bosAciklama={
            yazabilir
              ? "Süzgeçleri genişletin veya yeni personel ekleyin."
              : "Süzgeçleri genişletin."
          }
          yenile={() => void sorgu.refetch()}
        >
          <Tablo>
            <TabloBaslik>
              <th scope="col">Personel</th>
              <th scope="col">Rol</th>
              <th scope="col">Noktalar</th>
              <th scope="col">Kimlik durumu</th>
              <th scope="col">
                <span className="gizli-erisilebilir">İşlem</span>
              </th>
            </TabloBaslik>
            <TabloGovde>
              {personeller.map((p) => (
                <tr key={p.id}>
                  <td>
                    <p className="m-0 font-semibold text-murekkep">
                      {p.ad} {p.soyad}
                    </p>
                    <p className="m-0 text-xs text-murekkep-3">
                      {p.eposta ?? "e-posta yok"}
                      {p.telefon ? ` · ${telefonGoster(p.telefon)}` : ""}
                      {!p.aktif ? " · pasif" : ""}
                    </p>
                  </td>
                  <td className="text-sm text-murekkep-2">
                    {ROL_ADI[p.rol] ?? "Bilinmiyor"}
                  </td>
                  <td className="text-sm text-murekkep-2">
                    {(() => {
                      const hepsi = p.noktalar ?? [];
                      const yururlukte = hepsi.filter(
                        (a) => !atamaBitmisMi(a, bugun),
                      );
                      const gecmis = hepsi.length - yururlukte.length;
                      if (yururlukte.length === 0) {
                        return (
                          <span className="text-murekkep-3">
                            Atama yok
                            {gecmis > 0 && ` · ${gecmis} geçmiş`}
                          </span>
                        );
                      }
                      return (
                        <ul className="m-0 flex list-none flex-col gap-1 p-0">
                          {yururlukte.map((a) => (
                            <li
                              key={a.nokta_id}
                              className="flex items-center gap-2"
                            >
                              <span>{a.nokta_ad}</span>
                              <span className="text-xs text-murekkep-3">
                                {ATAMA_ROLU_ADI[a.rol] ?? ""}
                              </span>
                              {a.vekalet && (
                                <Rozet tur={a.gecerli ? "uyari" : "notr"}>
                                  vekalet {tarihGoster(a.baslangic)}–
                                  {tarihGoster(a.bitis)}
                                </Rozet>
                              )}
                            </li>
                          ))}
                          {gecmis > 0 && (
                            <li className="text-xs text-murekkep-3">
                              {gecmis} geçmiş atama (Düzenle&apos;de)
                            </li>
                          )}
                        </ul>
                      );
                    })()}
                  </td>
                  <td>
                    <span className="flex flex-wrap gap-1">
                      <Rozet tur={p.parola_kurulu ? "onay" : "notr"}>
                        parola {p.parola_kurulu ? "kurulu" : "yok"}
                      </Rozet>
                      <Rozet tur={p.pin_kurulu ? "onay" : "uyari"}>
                        PIN {p.pin_kurulu ? "kurulu" : "yok"}
                      </Rozet>
                      {p.totp_kurulu && <Rozet tur="bilgi">2FA</Rozet>}
                    </span>
                  </td>
                  <td className="text-right">
                    {yazabilir && (
                      <Buton
                        tur="sessiz"
                        onClick={() => {
                          setDuzenlenen(p);
                          setYeniAcik(false);
                        }}
                      >
                        Düzenle
                      </Buton>
                    )}
                  </td>
                </tr>
              ))}
            </TabloGovde>
          </Tablo>
        </SorguDurumu>
      </Kart>

      <PinBilgisi yazabilir={yazabilir} personeller={personeller} />
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

function PinBilgisi({
  yazabilir,
  personeller,
}: {
  yazabilir: boolean;
  personeller: PersonelKaydi[];
}) {
  const [personelId, setPersonelId] = useState("");
  const [sir, setSir] = useState<{
    tur: "Geçici parola" | "Geçici PIN";
    deger: string;
    sonKullanma: string;
  } | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [islemde, setIslemde] = useState(false);
  const istemci = useQueryClient();
  // Kendi hesabı listede yoktur: sıfırlama oturum sürümünü artırır ve kendi
  // oturumunu düşürür (kabul turu #25); sunucu da reddeder.
  const kendiId = aktifOturum(oturumDurumu())?.personel.id ?? null;

  const uret = async (tur: "parola" | "pin") => {
    if (!personelId) return;
    setIslemde(true);
    setHata(null);
    try {
      const yanit = await apiYaz<{
        gecici_parola?: string;
        gecici_pin?: string;
        son_kullanma_zamani: string;
      }>("POST", `/personel/${personelId}/gecici-${tur}`);
      setSir({
        tur: tur === "parola" ? "Geçici parola" : "Geçici PIN",
        deger: yanit.gecici_parola ?? yanit.gecici_pin ?? "",
        sonKullanma: yanit.son_kullanma_zamani,
      });
      // Listedeki "PIN yok / parola yok" rozeti eskimesin.
      void istemci.invalidateQueries({ queryKey: ["personel"] });
    } catch (e) {
      setHata((e as Error).message);
    } finally {
      setIslemde(false);
    }
  };

  return (
    <Kart baslik="PIN ve parola sıfırlama">
      <Uyari tur="bilgi" baslik="Sırlar geri okunamaz">
        Parola ve PIN Argon2id ile saklanır. Geçici değer 24 saat geçerlidir,
        yalnızca aşağıdaki başarılı yanıtta bir kez gösterilir ve ilk kullanımda
        değiştirilir. Geçici PIN offline terminal senkronuna gönderilmez. Kendi
        hesabınız listede yoktur; kendi sırrınızı başka bir yetkili sıfırlar.
      </Uyari>
      {yazabilir && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="flex min-w-64 flex-col gap-1 text-sm">
            Personel
            <select
              className="h-9 rounded-md border border-cizgi bg-kagit px-3"
              value={personelId}
              onChange={(e) => setPersonelId(e.target.value)}
            >
              <option value="">Personel seçin</option>
              {personeller
                .filter((p) => p.aktif && p.id !== kendiId)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.ad} {p.soyad}
                  </option>
                ))}
            </select>
          </label>
          <Buton
            disabled={!personelId || islemde}
            onClick={() => void uret("parola")}
          >
            Geçici parola üret
          </Buton>
          <Buton
            disabled={!personelId || islemde}
            onClick={() => void uret("pin")}
          >
            Geçici PIN üret
          </Buton>
        </div>
      )}
      {hata && <p className="mt-3 text-sm text-hata">{hata}</p>}
      {sir && (
        <Uyari tur="uyari" baslik={`${sir.tur} — şimdi kopyalayın`}>
          {/* key: yeni sır üretilince kopyalandı durumu sıfırlansın */}
          <SirKopyala key={sir.deger} deger={sir.deger} etiket={sir.tur} />
          <p className="mb-0 text-sm">
            Son kullanım: {tarihGoster(sir.sonKullanma)}
          </p>
          <Buton tur="sessiz" onClick={() => setSir(null)}>
            Ekrandan kaldır
          </Buton>
        </Uyari>
      )}
    </Kart>
  );
}

// ---------------------------------------------------------------------------

function PersonelFormu({
  personel,
  kullanilabilirRoller,
  kapat,
  kaydedildi,
}: {
  personel: PersonelKaydi | null;
  kullanilabilirRoller: number[];
  kapat: () => void;
  kaydedildi: () => void;
}) {
  const istemci = useQueryClient();
  const okul = useOkul();
  const bugun = yerelGun(
    new Date(),
    okul.data?.saat_dilimi ?? VARSAYILAN.SAAT_DILIMI,
  );
  const gecmisAtamalar = (personel?.noktalar ?? []).filter((a) =>
    atamaBitmisMi(a, bugun),
  );
  const [ad, setAd] = useState(personel?.ad ?? "");
  const [soyad, setSoyad] = useState(personel?.soyad ?? "");
  const [eposta, setEposta] = useState(personel?.eposta ?? "");
  const [telefon, setTelefon] = useState(telefonMaske(personel?.telefon ?? ""));
  const [rol, setRol] = useState(
    String(
      personel?.rol ?? kullanilabilirRoller[0] ?? Rol.OKUL_SISTEM_YONETICISI,
    ),
  );
  const [aktif, setAktif] = useState(personel?.aktif ?? true);
  const [gonderildi, setGonderildi] = useState(false);

  const kaydet = useMutation({
    mutationFn: () => {
      const govde = {
        ad: ad.trim(),
        soyad: soyad.trim(),
        eposta: eposta.trim() || null,
        telefon: telefon.trim() || null,
        aktif,
      };
      return personel
        ? apiYaz<PersonelKaydi>("PATCH", `/personel/${personel.id}`, {
            ...govde,
            ...(Number.parseInt(rol, 10) !== personel.rol
              ? { rol: Number.parseInt(rol, 10) }
              : {}),
          })
        : apiYaz<PersonelKaydi>("POST", "/personel", {
            ...govde,
            rol: Number.parseInt(rol, 10),
            noktalar: [],
          });
    },
    onSuccess: () => {
      void istemci.invalidateQueries({ queryKey: ["personel"] });
      kaydedildi();
    },
  });

  const adHatasi = gonderildi && !ad.trim() ? "Ad zorunludur." : undefined;
  const soyadHatasi =
    gonderildi && !soyad.trim() ? "Soyad zorunludur." : undefined;

  return (
    <Kart
      baslik={
        personel
          ? `Personel düzenle: ${personel.ad} ${personel.soyad}`
          : "Yeni personel"
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (!ad.trim() || !soyad.trim()) return;
          kaydet.mutate();
        }}
      >
        <div className="grid gap-3 md:grid-cols-2">
          <Alan
            etiket="Ad"
            value={ad}
            onChange={(e) => setAd(e.target.value)}
            {...(adHatasi ? { hata: adHatasi } : {})}
          />
          <Alan
            etiket="Soyad"
            value={soyad}
            onChange={(e) => setSoyad(e.target.value)}
            {...(soyadHatasi ? { hata: soyadHatasi } : {})}
          />
          <Alan
            etiket="E-posta"
            type="email"
            value={eposta}
            onChange={(e) => setEposta(e.target.value)}
            ipucu="Panele giriş e-posta ile yapılır."
          />
          <TelefonAlani
            etiket="Telefon"
            value={telefon}
            onChange={setTelefon}
          />
          <Secim
            etiket="Rol"
            value={rol}
            onChange={(e) => setRol(e.target.value)}
            secenekler={[
              ...(personel && !kullanilabilirRoller.includes(personel.rol)
                ? [personel.rol]
                : []),
              ...kullanilabilirRoller,
            ].map((r) => ({
              deger: String(r),
              metin:
                personel?.rol === r && !kullanilabilirRoller.includes(r)
                  ? `${ROL_ADI[r] ?? "—"} (mevcut, kullanıma kapalı)`
                  : (ROL_ADI[r] ?? "—"),
            }))}
          />
        </div>

        <Uyari tur="uyari" baslik="Rol atamasında iki kural">
          <p className="m-0">
            Kendi rolünüzün <strong>üstünde</strong> bir rol atayamazsınız ve{" "}
            <strong>kendi rolünüzü</strong> değiştiremezsiniz; değişikliği başka
            bir yetkili yapmalıdır.
          </p>
        </Uyari>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={aktif}
            onChange={(e) => setAktif(e.target.checked)}
            className="h-4 w-4 accent-simit"
          />
          Personel aktif (pasif personel kasada PIN ile oturum açamaz)
        </label>

        {!personel && (
          <p className="m-0 text-sm text-murekkep-3">
            Kayıt sonrası parola ve PIN henüz tanımlı değildir; kimlik akışından
            kurulur. Nokta atamaları &quot;Satış noktaları&quot; ekranından
            yapılır.
          </p>
        )}

        {gecmisAtamalar.length > 0 && (
          <section className="flex flex-col gap-1 text-sm">
            <h3 className="m-0 text-sm font-semibold text-murekkep-2">
              Geçmiş atamalar
            </h3>
            <p className="m-0 text-xs text-murekkep-3">
              Süresi dolmuş vekaletler; listede gösterilmez, denetim izi için
              burada kalır.
            </p>
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {gecmisAtamalar.map((a) => (
                <li
                  key={`${a.nokta_id}-${a.baslangic ?? ""}-${a.bitis ?? ""}`}
                  className="flex flex-wrap items-center gap-2 text-murekkep-2"
                >
                  <span>{a.nokta_ad}</span>
                  <span className="text-xs text-murekkep-3">
                    {ATAMA_ROLU_ADI[a.rol] ?? ""}
                  </span>
                  <Rozet tur="notr">
                    {tarihGoster(a.baslangic)}–{tarihGoster(a.bitis)}
                  </Rozet>
                </li>
              ))}
            </ul>
          </section>
        )}

        <IslemDurumu hata={kaydet.error} />

        <div className="flex flex-wrap gap-2">
          <Buton type="submit" tur="birincil" yukleniyor={kaydet.isPending}>
            {personel ? "Değişiklikleri kaydet" : "Personeli kaydet"}
          </Buton>
          <Buton type="button" tur="ikincil" onClick={kapat}>
            Vazgeç
          </Buton>
        </div>
      </form>
    </Kart>
  );
}
